import apiClient from '@/lib/api-client';

// Types
export interface StockLevel {
    id: string;
    productId: string;
    productName?: string;
    variantId: string | null;
    variantName?: string | null;
    branchId: string;
    branchName?: string;
    quantityOnHand: string;
    reservedQuantity: string;
    availableQuantity: string;
    confirmationStatus: 'pending' | 'counted' | 'confirmed';
    lastCountedAt: string | null;
    lowStockThreshold: number;
    baseUom: string;
    sku?: string | null;
    barcode?: string | null;
    updatedAt: string;
}

export interface StockLevelsResponse {
    data: StockLevel[];
    meta: { totalCount: number; cursor?: string | null; hasNextPage?: boolean };
}

export interface StockTransaction {
    id: string;
    productId: string;
    productName?: string;
    transactionType: string;
    quantity: string;
    unitCost?: string;
    batchNumber?: string;
    performerName?: string;
    branchName?: string;
    notes?: string;
    createdAt: string;
}

export interface StockAdjustment {
    productId: string;
    variantId?: string;
    branchId: string;
    quantity: string;
    uomId: string;
    adjustmentType: string;
    batchNumber?: string;
    expiryDate?: string;
    serialNumber?: string;
    unitCost?: string;
    notes: string;
}

export interface ConfirmationStatus {
    total: number;
    pending: number;
    counted: number;
    confirmed: number;
    lowStock: number;    // ADDED
    outOfStock: number;  // ADDED
}

export interface LowStockAlert {
    productId: string;
    productName?: string;
    branchId: string;
    branchName?: string;
    quantityOnHand: string;
    availableQuantity: string;
    lowStockThreshold: number;
    reorderPoint: number | null;
    reorderQuantity: number | null;
}

export interface StockLevelsParams {
    branchId?: string;
    search?: string;
    lowStock?: boolean;
    outOfStock?: boolean;
    productId?: string;
    limit?: number;
    page?: number;
    sortBy?: string;
    order?: string;
    confirmationStatus?: string;
}

export interface TransactionsParams {
    limit?: number;
    page?: number;
    transactionType?: string;
    branchId?: string;
    productId?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
}

// Stock Levels
export async function getStockLevels(
    tenantId: string,
    params: StockLevelsParams = {}
): Promise<StockLevelsResponse> {
    const response = await apiClient.get(`/tenants/${tenantId}/stock/levels`, {
        params: {
            branchId: params.branchId || undefined,
            search: params.search || undefined,
            lowStock: params.lowStock || undefined,
            outOfStock: params.outOfStock || undefined,
            productId: params.productId || undefined,
            limit: params.limit || 30,
            page: params.page || 1,
            sortBy: params.sortBy || 'updatedAt',
            order: params.order || 'desc',
            confirmationStatus: params.confirmationStatus || undefined,
        },
    });

    const responseData = response.data;
    if (responseData?.data && Array.isArray(responseData.data)) {
        return {
            data: responseData.data,
            meta: {
                totalCount: responseData.meta?.totalCount || responseData.data.length,
                hasNextPage: responseData.meta?.hasNextPage || false,
                cursor: responseData.meta?.cursor || null,
            },
        };
    }
    return { data: [], meta: { totalCount: 0, hasNextPage: false } };
}

export async function getProductStock(tenantId: string, productId: string, branchId?: string) {
    const response = await apiClient.get(`/tenants/${tenantId}/stock/levels/${productId}`, {
        params: { branchId: branchId || undefined }
    });
    return response.data?.data;
}

// Stock Adjustments
export async function createStockAdjustment(tenantId: string, data: StockAdjustment) {
    const response = await apiClient.post(`/tenants/${tenantId}/stock/adjustments`, data);
    return response.data?.data;
}

// Transactions
export async function getStockTransactions(
    tenantId: string,
    params: TransactionsParams = {}
): Promise<{ data: StockTransaction[]; meta: { totalCount: number; hasNextPage: boolean } }> {
    const response = await apiClient.get(`/tenants/${tenantId}/stock/transactions`, {
        params: {
            limit: params.limit || 50,
            page: params.page || 1,
            transactionType: params.transactionType || undefined,
            branchId: params.branchId || undefined,
            productId: params.productId || undefined,
            search: params.search || undefined,
            startDate: params.startDate || undefined,
            endDate: params.endDate || undefined,
        },
    });

    const responseData = response.data;
    if (responseData?.data && Array.isArray(responseData.data)) {
        return {
            data: responseData.data,
            meta: {
                totalCount: responseData.meta?.totalCount || responseData.data.length,
                hasNextPage: responseData.meta?.hasNextPage || false,
            },
        };
    }
    return { data: [], meta: { totalCount: 0, hasNextPage: false } };
}

// Confirmation
export async function confirmStock(tenantId: string, branchId: string, productIds?: string[]) {
    const response = await apiClient.post(`/tenants/${tenantId}/stock/confirm`, { branchId, productIds });
    return response.data?.data;
}

export async function getStockConfirmationStatus(tenantId: string, branchId: string): Promise<ConfirmationStatus> {
    const response = await apiClient.get(`/tenants/${tenantId}/stock/confirmation-status`, {
        params: { branchId: branchId || undefined }
    });
    return response.data?.data;
}

// Reports
export async function getLowStockAlerts(tenantId: string, branchId?: string): Promise<LowStockAlert[]> {
    const response = await apiClient.get(`/tenants/${tenantId}/stock/reports/low-stock`, {
        params: { branchId: branchId || undefined }
    });
    return response.data?.data || [];
}

export async function getStockValuation(tenantId: string, branchId?: string) {
    const response = await apiClient.get(`/tenants/${tenantId}/stock/reports/valuation`, {
        params: { branchId: branchId || undefined }
    });
    return response.data?.data || [];
}

// Bulk adjustment upload
export async function bulkAdjustStock(
    tenantId: string,
    branchId: string,
    adjustmentType: string,
    file: File
): Promise<{
    uploadId: string; totalRows: number; successCount: number; errorCount: number;
    errors: any[]; hasErrors: boolean;
}> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('branchId', branchId);
    formData.append('adjustmentType', adjustmentType);

    const response = await apiClient.post(
        `/tenants/${tenantId}/stock/adjustments/bulk-upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data?.data || response.data;
}

// Download stock template
export async function downloadStockTemplate(
    tenantId: string,
    type: 'adjustment' | 'receiving' = 'adjustment',
    branchId?: string
): Promise<Blob> {
    const params: Record<string, string> = {};
    if (branchId) params.branchId = branchId;
    const response = await apiClient.get(`/tenants/${tenantId}/stock/templates/${type}`, {
        params, responseType: 'blob'
    });
    return response.data;
}

// Export stock levels
export async function exportStockLevels(
    tenantId: string,
    params: { branchId?: string; lowStock?: boolean; outOfStock?: boolean } = {}
): Promise<Blob> {
    const response = await apiClient.get(`/tenants/${tenantId}/stock/export/levels`, {
        params, responseType: 'blob'
    });
    return response.data;
}

// Download bulk error report
export async function downloadStockBulkErrorReport(tenantId: string, uploadId: string): Promise<Blob> {
    const response = await apiClient.get(`/tenants/${tenantId}/stock/bulk-upload/${uploadId}/errors`, {
        responseType: 'blob'
    });
    return response.data;
}